import { createContext, type MouseEvent, useContext, useEffect, useId } from 'react';
import type { MenuItem, MenuScope } from './menu.js';

/**
 * Kaydedilmemiş değişiklik koruması (M6f G1-11): formlar kirli olduklarını bildirir; kabuk
 * üzerinden gezinme kirli formda onay bekler ("Discard unsaved changes?" Stay / Discard).
 */
export interface NavigationGuard {
  /** Bir formun kirli olup olmadığını kaydeder (`useUnsavedChanges`). */
  setDirty(key: string, dirty: boolean): void;
  isDirty(): boolean;
  /** Temizse hemen gider; kirliyse gitmeyi bekletir ve onay ister. */
  request(go: () => void): void;
  /** Onay bekleyen gezinme var mı (onay penceresi açık). */
  hasPending(): boolean;
  /** "Discard": kirli kayıtlar silinir, bekleyen gezinme yapılır. */
  confirm(): void;
  /** "Stay": bekleyen gezinme iptal, form olduğu gibi kalır. */
  cancel(): void;
  subscribe(listener: () => void): () => void;
}

export function createNavigationGuard(): NavigationGuard {
  const dirty = new Set<string>();
  const listeners = new Set<() => void>();
  let pending: (() => void) | null = null;
  const emit = () => {
    for (const listener of listeners) listener();
  };
  return {
    setDirty(key, value) {
      if (value) dirty.add(key);
      else dirty.delete(key);
    },
    isDirty: () => dirty.size > 0,
    request(go) {
      if (dirty.size === 0) {
        go();
        return;
      }
      pending = go;
      emit();
    },
    hasPending: () => pending !== null,
    confirm() {
      const go = pending;
      pending = null;
      dirty.clear();
      emit();
      go?.();
    },
    cancel() {
      if (pending === null) return;
      pending = null;
      emit();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/** Kabuğun içindeki ekranlara: hangi uygulama, hangi yol, nasıl gidilir. */
export interface ShellContextValue {
  scope: MenuScope;
  path: string;
  /** Konum sorgusu (ör. `?user=…`); yoksa `''`. */
  search: string;
  /** Uygulama içinde gider; kirli formda önce onay ister. */
  navigate: (path: string) => void;
  /** Kaydedilmemiş değişiklik koruması; kabuk verir. */
  guard?: NavigationGuard | undefined;
  /** Kabuğun menüsü (breadcrumb); yoksa müşteri menüsü `MENU`. */
  menu?: readonly MenuItem[] | undefined;
  /** Breadcrumb'ın ev ikonunun yolu; yoksa `/dashboard`. */
  homePath?: string | undefined;
}

export const ShellContext = createContext<ShellContextValue | null>(null);

export function useShell(): ShellContextValue | null {
  return useContext(ShellContext);
}

/**
 * Formun kaydedilmemiş değişikliği varken kabuk üzerinden gezinme (menü, breadcrumb, arama, hızlı
 * eylemler) onay ister. Form kapanınca kayıt kendiliğinden silinir. Kabuğun dışında etkisizdir.
 */
export function useUnsavedChanges(dirty: boolean): void {
  const guard = useShell()?.guard;
  const key = useId();
  useEffect(() => {
    if (!guard) return;
    guard.setDirty(key, dirty);
    return () => guard.setDirty(key, false);
  }, [guard, key, dirty]);
}

/**
 * Bağlantı tıklaması uygulama içinde yönlenir; Ctrl/⌘, Shift ya da orta tuşla tarayıcının kendi
 * davranışı (yeni sekme, yeni pencere) korunur. Alt+tık da uygulama içinde gider: Chromium'da
 * Alt+tık bağlantıyı indirir, gemi uygulamasında bu bir dosya penceresi açardı (M6f G1-19).
 */
export function followLink(
  event: MouseEvent<HTMLAnchorElement>,
  path: string,
  navigate: (path: string) => void,
): void {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
    return;
  }
  event.preventDefault();
  navigate(path);
}
